import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CardContainer } from './style'
import { theme } from 'theme'
import Text from 'particles/Text'
import { useNewTranslation } from 'hooks/index'
import Divider from 'components/Shared/Divider'
import SummaryContainer from './SummaryContainer'
import { selectRegistrationForm } from 'state/registrations'
import { exportOverview, selectSummaryById } from 'state/campaigns'
import { daysToDate } from 'services/dates'
import { getColumns as getRegistrationColumns, getRows as getRegistrationRows } from './RegistrationSummaryTableDef'
import { getColumns as getSubmissionColumns, getRows as getSubmissionRows } from './SubmissionSummaryTableDef'
import PartiTable from 'components/Shared/MuiTable/PartiTable'
import { selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import InfoTooltip from 'components/InfoTooltip'
import { getDayLeftDisplay } from 'components/utils'
import { selectAvailableStepStatus, GLOBAL_MODAL_TYPE, openModal } from 'state/constants'
import { ExportButton } from 'components/Shared/MuiTable/ActionButton'
import ExportModal from '../common/ExportModal'

interface OverviewProps {
    campaignUuid: string
}

const Overview: React.FC<OverviewProps> = ({ campaignUuid }) => {
    const dispatch = useDispatch()
    const [newT] = useNewTranslation()
    const registrationDetail = useSelector(selectRegistrationForm)
    const campaignSummary = useSelector(selectSummaryById)[campaignUuid]
    const registrationLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.GetRegistrationForm]))
    const isExportRequesting = useSelector(selectRequestsAreLoading([STATE_KEYS.ExportOverview]))
    const stepTypeAvailabilityStatus = useSelector(selectAvailableStepStatus)

    const registrationTimeWindow = registrationDetail ? registrationDetail.time_window : null
    const registrationDayLeft =
        registrationTimeWindow && registrationTimeWindow.end && campaignSummary
            ? daysToDate(registrationTimeWindow.end, campaignSummary.country)
            : undefined
    const registrationDayLeftDisplay = getDayLeftDisplay(newT, registrationDayLeft, 'statusLabel.registrationEnded')
    const submissionTimeWindow = campaignSummary?.overall_step_time_window || null
    const submissionDaysLeft =
        submissionTimeWindow?.end && campaignSummary
            ? daysToDate(submissionTimeWindow.end, campaignSummary.country)
            : undefined
    const submissionDayLeftDisplay = getDayLeftDisplay(newT, submissionDaysLeft, 'statusLabel.notAcceptingSubmission')
    const rowHeight = 56
    const registrationColumns = getRegistrationColumns(newT)
    const registration_result_summary = campaignSummary?.registration_result_summary
    const registrationRows = getRegistrationRows(
        campaignUuid,
        registration_result_summary?.pending_count.toString() ?? '-',
        registration_result_summary?.awaiting_response_count.toString() ?? '-',
        registration_result_summary?.user_accepted_count.toString() ?? '-',
        registration_result_summary?.joined_count.toString() ?? '-',
    )

    const submissionColumns = getSubmissionColumns(newT)
    const submissionRows = getSubmissionRows(campaignSummary.tasks, stepTypeAvailabilityStatus)

    const registrationUsedSlot =
        (registration_result_summary?.awaiting_response_count ?? 0) +
        (registration_result_summary?.user_accepted_count ?? 0) +
        (registration_result_summary?.joined_count ?? 0)

    const registrationSlotLeft = (registrationDetail?.max_slots ?? 0) - registrationUsedSlot
    return (
        <CardContainer padding={theme.newSpacing.l} showBorder={false}>
            <div className="title-container">
                <Text scale="H4" color="primary">
                    {newT('submission.review.campaignOverviewTitle')}
                </Text>
                <ExportButton onClick={() => dispatch(openModal(GLOBAL_MODAL_TYPE.REQUEST_EXPORT))} />
            </div>
            <Divider />
            {registrationDetail && (
                <SummaryContainer
                    title={newT('submission.review.registrationInsightsHeader')}
                    description={registrationDayLeftDisplay.text}
                    descriptionColor={registrationDayLeftDisplay.color}
                >
                    <div className="registration-slot-left">
                        <Text scale="bodyM500" color="primary">
                            {newT('statusLabel.registrationSlotLeft', { count: registrationSlotLeft })}
                        </Text>
                        <InfoTooltip
                            marginLeft={'0'}
                            label={newT('statusLabel.registrationSlotLeftTooltipDescription')}
                        />
                    </div>

                    <div className="registration-table">
                        <PartiTable
                            tableHeight="114px"
                            rowHeight={rowHeight}
                            columns={registrationColumns}
                            rows={registrationRows}
                            isLoading={registrationLoading}
                            hideToolbar={true}
                            disableColumnMenu
                            hideFooter={true}
                            noPadding={true}
                            hideTableBorder={true}
                            isNewStyle
                        />
                    </div>
                </SummaryContainer>
            )}

            <SummaryContainer
                title={newT('submission.review.submissionInsightsHeader')}
                description={submissionDayLeftDisplay.text}
                descriptionColor={submissionDayLeftDisplay.color}
            >
                <div className="submission-table">
                    <PartiTable
                        rowHeight={rowHeight}
                        // dynamic row height, overwrite rowHeight
                        getRowHeight={() => 'auto'}
                        tableHeight="auto"
                        columns={submissionColumns}
                        rows={submissionRows}
                        isLoading={registrationLoading}
                        hideToolbar={true}
                        disableColumnMenu
                        hideFooter={true}
                        noPadding={true}
                        hideTableBorder={true}
                        isNewStyle
                    />
                </div>
            </SummaryContainer>
            <ExportModal
                tableTitle={newT('submission.review.campaignOverviewTitle')}
                isRequesting={isExportRequesting}
                onClickExport={() => dispatch(exportOverview({ uuid: campaignUuid }))}
            />
        </CardContainer>
    )
}

export default Overview
